import {
  ROCKET_READER_PLANETS,
  firstChallengeIndexForPlanet,
  rocketReaderChallenges,
} from '@/data/rocketReaderChallenges';

export const ROCKET_READER_STORAGE_KEY = 'alhudah:rocket-reader-progress:v1';

export type RocketReaderProgress = {
  currentChallengeIndex: number;
  currentPlanetIndex: number;
  fuel: number;
  stars: number;
  completedLaunches: number;
  answeredCorrectCount: number;
};

export function createInitialRocketReaderProgress(): RocketReaderProgress {
  return {
    currentChallengeIndex: firstChallengeIndexForPlanet(ROCKET_READER_PLANETS[0].name),
    currentPlanetIndex: 0,
    fuel: 0,
    stars: 0,
    completedLaunches: 0,
    answeredCorrectCount: 0,
  };
}

function readNumber(value: unknown, fallback: number, min: number, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeProgress(value: unknown): RocketReaderProgress {
  const initial = createInitialRocketReaderProgress();
  if (!isRecord(value)) return initial;

  const currentPlanetIndex = readNumber(
    value.currentPlanetIndex,
    initial.currentPlanetIndex,
    0,
    ROCKET_READER_PLANETS.length - 1,
  );
  const currentPlanet = ROCKET_READER_PLANETS[currentPlanetIndex];
  const storedChallengeIndex = readNumber(
    value.currentChallengeIndex,
    initial.currentChallengeIndex,
    0,
    rocketReaderChallenges.length - 1,
  );
  const currentChallengeIndex =
    rocketReaderChallenges[storedChallengeIndex]?.planet === currentPlanet.name
      ? storedChallengeIndex
      : firstChallengeIndexForPlanet(currentPlanet.name);

  return {
    currentChallengeIndex,
    currentPlanetIndex,
    fuel: readNumber(value.fuel, initial.fuel, 0, 100),
    stars: readNumber(value.stars, initial.stars, 0, 9999),
    completedLaunches: readNumber(value.completedLaunches, initial.completedLaunches, 0, 9999),
    answeredCorrectCount: readNumber(
      value.answeredCorrectCount,
      initial.answeredCorrectCount,
      0,
      9999,
    ),
  };
}

export function loadRocketReaderProgress(): RocketReaderProgress {
  if (typeof window === 'undefined') return createInitialRocketReaderProgress();

  try {
    const stored = window.localStorage.getItem(ROCKET_READER_STORAGE_KEY);
    if (!stored) return createInitialRocketReaderProgress();
    return sanitizeProgress(JSON.parse(stored));
  } catch {
    return createInitialRocketReaderProgress();
  }
}

export function saveRocketReaderProgress(progress: RocketReaderProgress) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ROCKET_READER_STORAGE_KEY, JSON.stringify(progress));
}

export function clearRocketReaderProgress() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ROCKET_READER_STORAGE_KEY);
}
